import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { LOGO_URL } from '../../mock/mock';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ShieldCheck, User, Lock, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const AdminLogin = () => {
  const { adminLogin } = useAuth();
  const settings = useSettings();
  const logoSrc = settings?.general?.logo_url || LOGO_URL;
  const siteName = settings?.general?.site_name || 'TrainPanda';
  const nav = useNavigate();
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!username || !password) {
      toast({ title: 'Enter credentials', description: 'Please enter both username and password.' });
      return;
    }
    setLoading(true);
    try {
      await adminLogin(username, password);
      nav('/admin');
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Invalid credentials',
        description: 'Please check your username and password.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-100">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-8">
            <Link to="/" className="inline-block transition-transform hover:scale-105 mb-4">
              <img src={logoSrc} alt={siteName} className="h-12 w-auto object-contain" />
            </Link>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase bg-violet-50 text-violet-700 border border-violet-100">
              <ShieldCheck size={14} className="text-violet-600" />
              <span>Admin Management Portal</span>
            </div>

            <h1 className="text-2xl font-black text-slate-900 tracking-tight mt-3">
              Sign In to Dashboard
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Enter your administrator credentials to continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Username
              </label>
              <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3.5 focus-within:border-violet-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-500/20 transition-all">
                <User size={18} className="text-slate-400 shrink-0" />
                <Input
                  data-testid="admin-username-input"
                  autoComplete="username"
                  placeholder="e.g. admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="border-0 bg-transparent focus-visible:ring-0 px-0 h-11 text-sm font-medium text-slate-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3.5 focus-within:border-violet-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-500/20 transition-all">
                <Lock size={18} className="text-slate-400 shrink-0" />
                <Input
                  data-testid="admin-password-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-0 bg-transparent focus-visible:ring-0 px-0 h-11 text-sm font-medium text-slate-800"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-slate-400 hover:text-slate-600 focus:outline-none p-1 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-extrabold h-11 rounded-xl shadow-lg shadow-violet-500/25 transition-all mt-3 cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" size={18} /> Authenticating...
                </span>
              ) : (
                'Sign In to Console'
              )}
            </Button>
          </form>

          {/* Footer link */}
          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-violet-600 transition-colors"
            >
              <ArrowLeft size={14} /> Back to TrainPanda Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
